"use client";

import React, { useState } from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  VariableProductAddToCart,
  ProductPrice,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Tag, FolderTree, PackageOpen } from "lucide-react";

function ProductDetailConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();
  const [activeImage, setActiveImage] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-7">
          <div className="aspect-4/3 w-full rounded-md bg-muted/40 animate-pulse border border-border" />
          <div className="flex gap-3">
            {[0, 1, 2, 3].map((_, i) => (
              <div
                key={i}
                data-index={i}
                className="h-20 w-20 rounded-md bg-muted/40 animate-pulse border border-border"
              />
            ))}
          </div>
        </div>
        <div className="space-y-6 lg:col-span-5">
          <div className="h-8 w-3/4 rounded bg-muted/40 animate-pulse" />
          <div className="h-6 w-1/3 rounded bg-muted/40 animate-pulse" />
          <div className="h-24 w-full rounded bg-muted/40 animate-pulse" />
          <div className="h-11 w-full rounded bg-muted/40 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-6 text-center">
        <p className="text-sm text-foreground">
          Unable to load product information.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 inline-flex text-xs font-semibold text-primary underline underline-offset-4"
        >
          Try loading again
        </button>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-md border border-border bg-card p-12 text-center">
        <PackageOpen className="size-12 text-muted-foreground mb-3" />
        <h2 className="text-xl font-semibold text-foreground">Product not found</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The requested workshop tool or equipment item is currently unavailable.
        </p>
        <Link
          to="/ShopPage"
          className="mt-6 inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground shadow-xs hover:bg-primary/90 transition-colors"
        >
          Browse catalog
        </Link>
      </div>
    );
  }

  const product = products[0];
  const images = product.gallery_image_urls?.length
    ? product.gallery_image_urls
    : product.featured_image_url
    ? [product.featured_image_url]
    : [];
  const currentMainImage = activeImage ?? images[0];

  return (
    <div className="space-y-12">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
        {/* Left Column: Product Gallery */}
        <div className="space-y-4 lg:col-span-7">
          <div className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
            {currentMainImage ? (
              <div className="aspect-4/3 w-full overflow-hidden bg-muted/20">
                <img
                  src={currentMainImage}
                  alt={product.title}
                  className="h-full w-full object-contain p-4 transition-transform duration-300 hover:scale-105"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="flex aspect-4/3 w-full items-center justify-center bg-muted/20 text-muted-foreground">
                <PackageOpen className="size-16 stroke-1" />
              </div>
            )}
          </div>

          {images.length > 1 && (
            <div
              className="flex flex-wrap gap-3"
              role="group"
              aria-label="Product thumbnails"
            >
              {images.map((url, i) => {
                const isSelected = url === currentMainImage;
                return (
                  <button
                    key={url + i}
                    data-index={i}
                    type="button"
                    onClick={() => setActiveImage(url)}
                    aria-label={`${product.title} view ${i + 1}`}
                    aria-current={isSelected ? "true" : undefined}
                    className={`h-20 w-20 shrink-0 overflow-hidden rounded-md border bg-card p-1 transition-all ${
                      isSelected
                        ? "border-primary ring-2 ring-primary/40"
                        : "border-border opacity-70 hover:opacity-100 hover:border-primary/50"
                    }`}
                  >
                    <img
                      src={url}
                      alt=""
                      aria-hidden="true"
                      className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Column: Title, Price, Attributes, Add to Cart */}
        <div className="flex flex-col justify-start space-y-6 lg:col-span-5">
          <div className="space-y-3">
            <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              {product.title}
            </h1>

            <div className="flex items-center gap-3">
              <ProductPrice
                product={product}
                as="div"
                className="text-2xl font-bold text-primary"
              />
              {product.on_sale && (
                <Badge variant="default" className="text-xs uppercase tracking-wider">
                  Sale
                </Badge>
              )}
            </div>
          </div>

          <Separator className="bg-border" />

          {/* Short Description */}
          {product.short_description && (
            <div
              className="text-sm leading-relaxed text-muted-foreground [&_p]:mb-2 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          {/* Variable Product / Add to Cart handler */}
          <div className="rounded-md border border-border bg-card p-5 shadow-xs">
            <VariableProductAddToCart
              product={product}
              onVariationResolved={(v) =>
                setActiveImage(v?.featured_image_url ?? null)
              }
              className="w-full space-y-4"
              buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 w-full h-11 text-base font-semibold shadow-xs"
            />
          </div>

          {/* Product Meta */}
          {((product.categories && product.categories.length > 0) ||
            (product.tags && product.tags.length > 0)) && (
            <div className="space-y-3 rounded-md border border-border bg-card/60 p-4 text-xs text-muted-foreground">
              {product.categories && product.categories.length > 0 && (
                <div className="flex items-start gap-2">
                  <FolderTree className="size-4 shrink-0 text-primary mt-0.5" />
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium text-foreground">
                      Categories:
                    </span>
                    {product.categories.map((cat, catIdx) => (
                      <Link
                        key={cat.id || cat.slug || catIdx}
                        to={cat.link || "#"}
                        className="text-muted-foreground transition-colors hover:text-primary underline-offset-4 hover:underline"
                      >
                        {cat.name}
                        {catIdx < product.categories.length - 1 ? "," : ""}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {product.tags && product.tags.length > 0 && (
                <div className="flex items-start gap-2">
                  <Tag className="size-4 shrink-0 text-primary mt-0.5" />
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium text-foreground">Tags:</span>
                    {product.tags.map((tag, tagIdx) => (
                      <Link
                        key={tag.id || tag.slug || tagIdx}
                        to={tag.link || "#"}
                        className="text-muted-foreground transition-colors hover:text-primary underline-offset-4 hover:underline"
                      >
                        {tag.name}
                        {tagIdx < product.tags.length - 1 ? "," : ""}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Full Long Description */}
      {product.long_description && (
        <div className="rounded-md border border-border bg-card p-6 md:p-8 shadow-xs">
          <h2 className="text-xl font-bold tracking-tight text-foreground mb-4">
            Product details and specifications
          </h2>
          <Separator className="mb-6 bg-border" />
          <div
            className="prose max-w-none text-sm leading-relaxed text-muted-foreground [&_p]:mb-4 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground [&_h3]:mt-6 [&_h3]:mb-2"
            dangerouslySetInnerHTML={{ __html: product.long_description }}
          />
        </div>
      )}
    </div>
  );
}

export default function SingleProduct() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};

  return (
    <section
      id="single-product-template-section"
      data-nav="dark"
      className="bg-background text-foreground py-12 md:py-16"
    >
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <EcommerceProductsProvider wp_query={currentWPQueryParams}>
          <ProductDetailConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}